import React, { useEffect, useContext, useRef, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import _ from "lodash";

import {
    LinearProgress,
    Grid,
    Button,
    Link,
    Typography,
    ListItemIcon,
    Tooltip

} from '@material-ui/core';

import {
    PageHeader
} from '../../common';

import {
    Toolbar,
    ContentGrid,
    ImportDialog,
    HelpDialog,

} from './components';

import { UserContext } from '../../contexts/UserContext';
import { PageProvider, PageContext } from './PageContext';
import { NavContext, NavRoutes } from '../../contexts';
import { ToastContext } from '../../contexts';

import { DataUpdateStatus, RowUpdateStatus } from '../../../helpers';

import AddIcon from '@material-ui/icons/Add';

import { makeStyles } from '@material-ui/core/styles';

import PencilIcon from '@material-ui/icons/EditOutlined';
import EyeIcon from '@material-ui/icons/VisibilityOutlined';
import { Filters } from './components/Filters';

const useStyles = makeStyles((theme) => ({
    root: {
    },
    toolbar: {
        marginTop: theme.spacing(3),
    },
    filters: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    contentGrid: {
        marginTop: theme.spacing(1),
        width: "100%",
        height: "100%"
    },
    infoButton: {
        marginLeft: theme.spacing(1)
    }
}));

const PageContent = (props) => {
    const classes = useStyles();

    const {
        state,
        pageIsReady,
        setPageIsReady,
        isBusy,
        setIsBusy,
        getTableAsync,
        loadTableRowsAsync,
        updateTableStateAsync,
        setFilter,
        resetTableStateAsync,
        updateTableContentAsync,
        importTableContentAsync,
        exportTableContentAsync,
        createGridDataSource,
        getGridDataSource,
        getSelectedNodes,
        gridApi,
        addNewRow,
        updateRow,
        updateRows,
        deleteRows,
        cancelRowChanges,
        stopEditingRows,
        refreshRowsStatus,
        loadMoreRows,
        clearGridStatus
    } = useContext(PageContext);

    var context = useContext(PageContext);

    const {
        state: { me: userInfo }
    } = useContext(UserContext);

    const {
        tables
    } = userInfo;

    const {
        navigateTo
    } = useContext(NavContext);

    const {
        showSuccessMessage,
        showErrorMessage,
        showWarningMessage
    } = useContext(ToastContext);

    const {
        table,
        notAvailable,
        dataTransaction,
        updateResult,
        gridState,
        lastGridState
    } = state;

    const [clearOnImport, setClearOnImport] = useState(false);

    const uploadFileAsync = async (data) => {
        setIsBusy({ isImporting: true });
        const success = await importTableContentAsync({ tableId: parseInt(tableId), data, clearOnImport: clearOnImport });
        setIsBusy({ isImporting: false });
        return success;
    }

    const loadRowsHandler = async (sortAndFilter, success) => {
        setIsBusy({ loadMore: true });
        const bundle = await loadTableRowsAsync(parseInt(tableId), sortAndFilter);
        success(bundle ? bundle.items : [], bundle ? bundle.totalItems : 0);
        setIsBusy({ loadMore: false });
    }

    const loadMoreHandler = (pageIndex, pageSize) => {
        loadMoreRows(pageIndex, pageSize);
    }

    const windowResizeHandler = (e) => {
        if (containerDiv.current) {
            const htmlDiv = containerDiv.current;
            const height = window.innerHeight - (htmlDiv.offsetTop + 64);
            htmlDiv.style.height = `${height}px`;
        }
    }

    const addNewRowHandler = () => {
        addNewRow();
    }

    const updateRowHandler = (rowIndex) => {
        updateRow(rowIndex);
    }

    const updateRowsHandler = (rowIndexes) => {
        updateRows(rowIndexes);
    }

    const deleteRowHandler = () => {
        const rowIndexes = (getSelectedNodes() ?? []).map(x => x.rowIndex);
        if (rowIndexes && rowIndexes.length > 0)
            deleteRows(rowIndexes);
    }

    const saveChangesHandler = async () => {
        setIsBusy({ saving: true });
        stopEditingRows(true);
        await updateTableContentAsync({
            tableId: parseInt(tableId),
            transaction: dataTransaction
        });
        setIsBusy({ saving: false });
    }

    const cancelChangesHandler = () => {
        cancelRowChanges(true);
    }

    const resetGridHandler = async () => {
        setFilter(null);
        setIsBusy({ resetGridState: true });
        await resetTableStateAsync(parseInt(tableId));
        clearGridStatus();
        setIsBusy({ resetGridState: false });
    }

    const importRowsHandler = () => {
        setIsBusy({ _importDialog: true });
    }

    const importDialogCloseHandler = () => {
        setIsBusy({ _importDialog: false });
    }

    const importDialogUploadedHandler = (file, success) => {
        if (success) {
            setIsBusy({ _importDialog: false });
            showSuccessMessage(`File '${file.name}' enqueued for import succesfully!`);
        }
    }

    const exportRowsHandler = async () => {
        setIsBusy({ isExporting: true });
        const success = await exportTableContentAsync({ tableId: parseInt(tableId) });
        setIsBusy({ isExporting: false });
        if (success) {
            showSuccessMessage("Your export request has been enqueued");
        }
    }

    const showHelpHandler = () => {
        setIsBusy({ _helpDialog: true });
    }

    const helpDialogCloseHandler = () => {
        setIsBusy({ _helpDialog: false });
    }

    const { tableId } = useParams();
    useEffect(() => {
        setPageIsReady(false);
        if (tableId) {
            getTableAsync(parseInt(tableId));
        }
    }, [tableId]);

    useEffect(() => {
        if (table) {
            setIsBusy({ loadMore: true });
            createGridDataSource({
                table: table,
                pageSize: 20,
                filterModel: gridState ? gridState.filterModel : null,
                onLoadRows: loadRowsHandler
            });
            setPageIsReady(table !== null);
        }
    }, [table]);

    const containerDiv = useRef(null);
    useEffect(() => {
        if (pageIsReady && containerDiv.current) {
            const htmlDiv = containerDiv.current;
            const height = window.innerHeight - (htmlDiv.offsetTop + 64);
            htmlDiv.style.height = `${height}px`;
        }
    }, [pageIsReady]);

    useEffect(() => {
        window.addEventListener("resize", windowResizeHandler);
        return () => {
            window.addEventListener("resize", null);
        }
    }, []);

    useEffect(() => {
        if (notAvailable) {
            navigateTo(NavRoutes.Forbidden);
        }
    }, [notAvailable]);

    useEffect(() => {
        if (updateResult) {
            refreshRowsStatus(updateResult);
            if (updateResult.status === DataUpdateStatus.Ok) {
                showSuccessMessage("All changes have been saved");
            } else if (updateResult.status === DataUpdateStatus.Error) {
                showErrorMessage(updateResult.message);
            } else if (updateResult.status === DataUpdateStatus.NoChanges) {
                showWarningMessage(updateResult.message);
            }
        }
    }, [updateResult]);

    const updateTableState = useCallback(_.debounce(async (tableId, newState, gridApi) => {
        setFilter(gridApi.getFilterModel());
        setIsBusy({ _savingGridState: true });
        await updateTableStateAsync({ tableId, newState });
        setIsBusy({ _savingGridState: false });
    }, 1000), [table]);

    useEffect(() => {
        if (gridState) {
            if (!isBusy("resetGridState")) {
                const newUserState = JSON.stringify(gridState);
                const lastUserState = JSON.stringify(lastGridState);
                if (newUserState != lastUserState) {
                    updateTableState(parseInt(tableId), newUserState, gridApi);
                }
            }
        }
    }, [gridState]);

    return (
        <>
            {pageIsReady ?
                <div>
                    <Grid container spacing={3} className={classes.root}>
                        <Grid item xs={12}>
                            <PageHeader
                                title={
                                    <div>
                                        {table.tableName}&nbsp;
                                        <ListItemIcon>
                                            {tables.find(x => x.tableName === table.tableName).roleIds.includes(1) || tables.find(x => x.tableName === table.tableName).roleIds.includes(2) ?
                                                (<Tooltip title="You can view and edit this table" arrow placement="top-end"><PencilIcon /></Tooltip>) :
                                                (<Tooltip title="You can only view this table" arrow placement="top-end"><EyeIcon /></Tooltip>)}
                                        </ListItemIcon>
                                    </div>
                                }
                                subtitle={table.description ?? "Table content"}
                                disabled={isBusy()}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Toolbar
                                className={classes.toolbar}
                                disabled={isBusy()}
                                onAddNewRow={addNewRowHandler}
                                onDeleteRow={deleteRowHandler}
                                onSave={saveChangesHandler}
                                onCancel={cancelChangesHandler}
                                onResetGrid={resetGridHandler}
                                onImportRows={importRowsHandler}
                                onExportRows={exportRowsHandler}
                                onShowHelp={showHelpHandler}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Filters
                                className={classes.filters}
                                disabled={isBusy()}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <div ref={containerDiv}>
                                <ContentGrid
                                    disabled={isBusy()}
                                    className={classes.contentGrid}
                                    dataSource={getGridDataSource()}
                                    onLoadMore={loadMoreHandler}
                                    onUpdateRow={updateRowHandler}
                                    onUpdateRows={updateRowsHandler}
                                />
                            </div>
                        </Grid>
                    </Grid>
                    <ImportDialog
                        open={isBusy("_importDialog")}
                        upload={uploadFileAsync}
                        onClose={importDialogCloseHandler}
                        onFileUploaded={importDialogUploadedHandler}
                        disabled={isBusy()}
                        handleChange={(value) => setClearOnImport(value)}
                    />
                    <HelpDialog
                        open={isBusy("_helpDialog")}
                        onClose={helpDialogCloseHandler}
                        disabled={isBusy()}
                    />
                </div>
                :
                <LinearProgress />
            }
        </>
    );
}

export const TableContentPage = () => {

    return (
        <PageProvider>
            <PageContent />
        </PageProvider>
    );
}